import * as THREE from 'three';
import { addEdgesToMesh } from './visualization';
import { Representation } from 'forma-embedded-view-sdk/dist/internal/representations';
import { Clipper, EndType, JoinType, Paths64 } from 'clipper2-ts';
import { applyClippingPlaneToScene } from './clipping';
import { buildingWindows } from './buildingWindows';
import { useThreeSceneStore } from '@/store/threeSceneStore';

export interface OrthoPreviewConfig {
	margin?: number;
}
const clipPlane = new THREE.Plane(new THREE.Vector3(0, 0, -1), 0);
export class OrthoPreviewCamera {
	public camera: THREE.OrthographicCamera;
	public previewScene: THREE.Scene;
	public targetObject: THREE.Object3D | null = null;
	public config: OrthoPreviewConfig;

	constructor(config: OrthoPreviewConfig = {}) {
		this.config = {
			margin: config.margin ?? 10,
		};
		this.previewScene = new THREE.Scene();
		this.camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 10000);
		this.camera.up.set(0, 1, 0); // North (Y+) at top
	}

	setTarget(
		object: THREE.Object3D,
		buildingGraph: Representation.GraphBuilding,
		mainScene?: THREE.Scene,
		terrain?: THREE.Object3D
	) {
		if (this.targetObject?.uuid === object.uuid) return;
		useThreeSceneStore.getState().setCurrentTarget(object);
		this.previewScene.clear();
		const cloned = object.clone();
		cloned.castShadow = true;
		cloned.receiveShadow = true;
		const bbox = new THREE.Box3().setFromObject(cloned);
		const center = bbox.getCenter(new THREE.Vector3());

		clipPlane.constant = 0;
		this.targetObject = cloned;

		addEdgesToMesh(cloned as THREE.Mesh, this.previewScene);

		this.previewScene.add(cloned);

		if (terrain) {
			const terrainClone = terrain.clone();
			terrainClone.castShadow = true;
			terrainClone.receiveShadow = true;
			// addEdgesToMesh(terrainClone as THREE.Mesh, this.previewScene);
			this.previewScene.add(terrainClone);
		}
		// Copy lights from main scene if provided
		if (mainScene) {
			mainScene.traverse((obj) => {
				if (obj instanceof THREE.Light) {
					const lightClone = obj.clone();
					lightClone.position.copy(obj.position);
					if (
						(obj instanceof THREE.DirectionalLight || obj instanceof THREE.SpotLight) &&
						lightClone instanceof THREE.Object3D &&
						(obj as THREE.DirectionalLight | THREE.SpotLight).target instanceof
							THREE.Object3D
					) {
						(
							lightClone as THREE.DirectionalLight | THREE.SpotLight
						).target.position.copy(
							(obj as THREE.DirectionalLight | THREE.SpotLight).target.position
						);
						this.previewScene.add(
							(lightClone as THREE.DirectionalLight | THREE.SpotLight).target
						);
					}
					this.previewScene.add(lightClone);
				}
			});
		}

		this.drawRoomFloorPolygons(cloned, buildingGraph, mainScene);
		this.updateCameraFrustum(center);
	}

	updateCameraFrustum(center?: THREE.Vector3) {
		if (!this.targetObject) return;

		const bbox = new THREE.Box3().setFromObject(this.targetObject);
		const size = bbox.getSize(new THREE.Vector3());
		const margin = this.config.margin ?? 10;

		const viewSize = Math.max(size.x, size.y) + margin * 2;

		this.camera.left = -viewSize / 2;
		this.camera.right = viewSize / 2;
		this.camera.top = viewSize / 2;
		this.camera.bottom = -viewSize / 2;

		const cameraZ = 100;
		const targetCenter = center ?? bbox.getCenter(new THREE.Vector3());

		// Camera above the object's center, looking down Z
		this.camera.position.set(targetCenter.x, targetCenter.y, cameraZ);
		this.camera.up.set(0, 1, 0); // North (Y+) at top
		this.camera.lookAt(targetCenter.x, targetCenter.y, targetCenter.z);

		this.camera.near = 0.1;
		this.camera.far = cameraZ + size.z / 2 + margin;
		this.camera.updateProjectionMatrix();
	}

	updateZPosition(level: number, levelHeight: number, scene: THREE.Scene) {
		let vertexZ = 0;
		const sceneObjects: THREE.Object3D[] = [];

		if (this.targetObject && (this.targetObject as THREE.Mesh).geometry) {
			const mesh = this.targetObject as THREE.Mesh;
			const positionAttr = mesh.geometry.attributes.position as THREE.BufferAttribute;
			let minZ = Infinity;
			for (let i = 0; i < positionAttr.count; i++) {
				const z = positionAttr.getZ(i);
				if (z < minZ) minZ = z;
			}
			vertexZ = minZ;
			scene.traverse((obj) => {
				if (obj.userData.path === this.targetObject?.userData.path) {
					sceneObjects.push(obj);
				}
			});
		}

		const clipPlaneConstant = (level ? level * levelHeight : 0) + vertexZ + levelHeight / 2;
		if (Math.abs(clipPlane.constant - clipPlaneConstant) < 0.01) {
			clipPlane.constant = 0;
			applyClippingPlaneToScene(scene, null);
			applyClippingPlaneToScene(this.previewScene, null);
			this.removeViewPointTargets(scene);
			this.camera.updateProjectionMatrix();
			return;
		}
		clipPlane.constant = clipPlaneConstant;

		applyClippingPlaneToScene(scene, clipPlane);
		applyClippingPlaneToScene(this.previewScene, clipPlane);

		this.removeViewPointTargets(scene);
		this.camera.updateProjectionMatrix();
	}

	clearTarget(scene?: THREE.Scene) {
		this.previewScene.clear();
		if (scene) {
			applyClippingPlaneToScene(scene, null);
		}
		applyClippingPlaneToScene(this.previewScene, null);
		this.targetObject = null;
	}

	addViewPointTargets(scene: THREE.Scene) {
		// Remove existing view point targets
		const existingTargets = scene.children.filter((child) => child.userData.isViewPointTarget);
		existingTargets.forEach((target) => {
			scene.remove(target);
			if (target instanceof THREE.Mesh) {
				target.geometry.dispose();
				if (Array.isArray(target.material)) {
					target.material.forEach((mat) => mat.dispose());
				} else {
					target.material.dispose();
				}
			}
		});

		// Iterate through all buildings and their windows
		scene.traverse((obj) => {
			if (!(obj instanceof THREE.Mesh) || obj.userData.category !== 'building') return;

			const buildingPath = obj.userData.path;
			if (!buildingPath) return;

			const windows = buildingWindows.get(buildingPath);
			if (!windows || windows.length === 0) return;

			// Create sphere for each window
			windows.forEach((windowData, index) => {
				const sphere = new THREE.Mesh(
					new THREE.SphereGeometry(0.3, 16, 16),
					new THREE.MeshBasicMaterial({
						color: 0x00ff00,
						transparent: true,
						opacity: 0.7,
					})
				);

				// Position at window center (already in world space)
				sphere.position.copy(windowData.position);

				// Store metadata
				sphere.userData.isViewPointTarget = true;
				sphere.userData.outsideDirection = windowData.normal.clone();
				sphere.userData.buildingId = obj.uuid;
				sphere.userData.buildingPath = buildingPath;
				sphere.userData.windowUuid = windowData.uuid;
				sphere.userData.windowIndex = index;

				sphere.name = `WindowViewPoint_${buildingPath}_${index}`;
				scene.add(sphere);
			});
		});
	}

	removeViewPointTargets(scene: THREE.Scene) {
		if (!scene || typeof scene !== 'object' || !Array.isArray(scene.children)) {
			console.warn('removeViewPointTargets: scene is undefined or not a THREE.Scene');
			return;
		}
		const toRemove: THREE.Object3D[] = [];
		for (const obj of scene.children) {
			if (obj.userData && obj.userData.isViewPointTarget) {
				toRemove.push(obj);
			}
		}
		toRemove.forEach((obj) => scene.remove(obj));
	}

	drawRoomFloorPolygons(
		object: THREE.Object3D,
		buildingGraph: Representation.GraphBuilding,
		scene?: THREE.Scene
	) {
		if (scene) {
			this.removeRoomFloorPolygons(scene);
		}

		const floorCount = buildingGraph.data.levels.length;
		let colorIdx = 0;
		const totalSpaces = buildingGraph.data.levels.reduce(
			(acc, level) => acc + (level.spaces?.length || 0),
			0
		);
		const spacesGroup = new THREE.Group();
		spacesGroup.name = 'RoomFloorPolygonsGroup';
		const wallThickness = 0.0;

		const allRoomPositions: number[] = [];

		let minX = Infinity;
		let minY = Infinity;
		let minZ = Infinity;
		let maxX = -Infinity;
		let maxY = -Infinity;

		if (object instanceof THREE.Mesh && object.geometry) {
			const posAttr = object.geometry.getAttribute('position');
			for (let i = 0; i < posAttr.count; i++) {
				const x = posAttr.getX(i);
				const y = posAttr.getY(i);
				const z = posAttr.getZ(i);
				if (x < minX) minX = x;
				if (y < minY) minY = y;
				if (z < minZ) minZ = z;
				if (x > maxX) maxX = x;
				if (y > maxY) maxY = y;
			}
		}

		let pointsMinX = Infinity;
		let pointsMinY = Infinity;
		let pointsMaxX = -Infinity;
		let pointsMaxY = -Infinity;

		for (let floor = 0; floor < floorCount; floor++) {
			const level = buildingGraph.data.levels[floor];
			const pointsObj = level.points;
			if (!pointsObj) continue;

			for (const pid in pointsObj) {
				const [x, y] = pointsObj[pid] ?? [0, 0];
				if (x < pointsMinX) pointsMinX = x;
				if (y < pointsMinY) pointsMinY = y;
				if (x > pointsMaxX) pointsMaxX = x;
				if (y > pointsMaxY) pointsMaxY = y;
			}
		}

		const buildingCenterX = (minX + maxX) / 2;
		const buildingCenterY = (minY + maxY) / 2;
		const pointsCenterX = (pointsMinX + pointsMaxX) / 2;
		const pointsCenterY = (pointsMinY + pointsMaxY) / 2;

		// Calculate offset from pointsObj space to building space
		const offsetX = buildingCenterX - pointsCenterX;
		const offsetY = buildingCenterY - pointsCenterY;

		spacesGroup.position.set(buildingCenterX, buildingCenterY, 0);
		for (let floor = 0; floor < floorCount; floor++) {
			const level = buildingGraph.data.levels[floor];
			const pointsObj = level.points;
			const surfaces = level.surfaces;
			const spaces = level.spaces;
			if (!pointsObj || !surfaces || !spaces) {
				console.warn(`Missing data for floor ${floor}`);
				continue;
			}

			const floorZ = minZ + floor * level.height;

			for (let spaceIdx = 0; spaceIdx < spaces.length; spaceIdx++) {
				const space = spaces[spaceIdx];
				const outerLoop = space.outerLoop;
				if (!Array.isArray(outerLoop) || outerLoop.length < 3) continue;
				const hue = (colorIdx * 360) / totalSpaces;
				const color = new THREE.Color();
				color.setHSL(hue / 360, 0.7, 0.5);
				colorIdx++;

				const pointIds: string[] = [];
				for (let i = 0; i < outerLoop.length; i++) {
					const loopItem = outerLoop[i];
					const surface = surfaces.find((s) => s.id === loopItem.surfaceId);
					if (!surface) continue;
					let pointId: string;
					if (loopItem.directionAToB) {
						pointId = surface.pointA;
					} else {
						pointId = surface.pointB;
					}
					pointIds.push(pointId);
				}
				if (pointIds[0] !== pointIds[pointIds.length - 1]) {
					pointIds.push(pointIds[0]);
				}

				const floorVertices: THREE.Vector2[] = [];
				for (const pid of pointIds) {
					const pt = pointsObj[pid];
					if (!pt) continue;
					const [x, y] = pt;
					floorVertices.push(new THREE.Vector2(x, y));
				}
				if (floorVertices.length < 3) continue;

				const paddedVertices = this.offsetPolygonInward(floorVertices, wallThickness);
				if (paddedVertices.length < 3) {
					console.warn(
						`Padding resulted in degenerate polygon for floor ${floor}, space ${spaceIdx}`
					);
					continue;
				}

				const shape = new THREE.Shape(paddedVertices);
				const extrudeSettings = {
					depth: level.height - wallThickness,
					bevelEnabled: false,
					steps: 1,
				};
				const extrudeGeometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);
				extrudeGeometry.computeBoundingBox();

				const roomPositionAttr = extrudeGeometry.getAttribute('position');
				const roomFloorZ = floorZ + wallThickness;
				const roomPositions: number[] = [];

				for (let i = 0; i < roomPositionAttr.count; i++) {
					const localX = roomPositionAttr.getX(i);
					const localY = roomPositionAttr.getY(i);
					const localZ = roomPositionAttr.getZ(i);

					const worldX = localX + offsetX;
					const worldY = localY + offsetY;
					const worldZ = localZ + roomFloorZ;

					roomPositions.push(worldX, worldY, worldZ);
					allRoomPositions.push(worldX, worldY, worldZ);
				}

				const fillMaterial = new THREE.MeshBasicMaterial({
					color: color,
					opacity: 0.0,
					transparent: true,
					depthWrite: false,
					depthTest: false,
				});
				const fillMesh = new THREE.Mesh(extrudeGeometry, fillMaterial);
				fillMesh.renderOrder = 999 + floorZ;
				fillMesh.position.set(
					offsetX - buildingCenterX,
					offsetY - buildingCenterY,
					roomFloorZ
				);
				fillMesh.userData.isRoomFloorFill = true;
				fillMesh.userData.floorIndex = floor;
				fillMesh.name = `RoomFloorFill_Floor${floor}_Space${spaceIdx}`;
				const unit = buildingGraph.data.units.find((u) => u.spaceIds?.includes(space.id));
				fillMesh.userData.unit = unit;
				fillMesh.userData.isSolid = false;
				fillMesh.userData.basePolygon = paddedVertices.map((v) => ({ x: v.x, y: v.y }));
				fillMesh.userData.color = color.getHexString();
				fillMesh.userData.floorZ = roomFloorZ;
				fillMesh.userData.path = space.id;
				fillMesh.userData.position = {
					itemSize: 3,
					type: 'Float32Array',
					array: roomPositions,
					normalized: false,
				};

				spacesGroup.add(fillMesh);
			}
		}

		spacesGroup.userData.position = {
			itemSize: 3,
			type: 'Float32Array',
			array: allRoomPositions,
			normalized: false,
		};

		this.previewScene.add(spacesGroup);
		if (scene) {
			scene.add(spacesGroup.clone());
		}

		spacesGroup.updateMatrixWorld(true);
	}

	private offsetPolygonInward(vertices: THREE.Vector2[], offset: number): THREE.Vector2[] {
		if (vertices.length < 3) return vertices;

		const SCALE = 1000; // Scale factor for precision (clipper2 uses integers)

		const path = vertices.map((v) => ({
			x: Math.round(v.x * SCALE),
			y: Math.round(v.y * SCALE),
		}));

		const solution: Paths64 = Clipper.inflatePaths(
			[path], // Array of paths
			-offset * SCALE, // Negative for inward offset
			JoinType.Miter, // Sharp corners
			EndType.Polygon // Closed polygon
		);

		if (!solution || solution.length === 0 || solution[0].length < 3) {
			console.warn('Clipper offset resulted in empty polygon, returning original');
			return vertices;
		}

		const offsetVertices = solution[0].map(
			(p) => new THREE.Vector2(Number(p.x) / SCALE, Number(p.y) / SCALE)
		);

		return offsetVertices;
	}

	removeRoomFloorPolygons(scene: THREE.Scene) {
		if (!scene || typeof scene !== 'object' || !Array.isArray(scene.children)) {
			console.warn('removeRoomFloorPolygons: scene is undefined or not a THREE.Scene');
			return;
		}
		const toRemove: THREE.Object3D[] = [];
		const spacesGroup = scene.getObjectByName('RoomFloorPolygonsGroup') as THREE.Group;
		if (!spacesGroup) return;
		for (const obj of spacesGroup.children) {
			if (obj.userData && (obj.userData.isRoomFloorFill || obj.userData.isRoomFloorOutline)) {
				toRemove.push(obj);
			}
		}
		for (const obj of toRemove) {
			spacesGroup.remove(obj);
		}
		scene.remove(spacesGroup);
		this.previewScene.remove(spacesGroup);
	}
}

export function renderOrthoPreview(
	renderer: THREE.WebGLRenderer,
	canvasWidth: number,
	canvasHeight: number,
	orthoPreview: OrthoPreviewCamera
) {
	if (!orthoPreview.targetObject) return;

	// Always render to (0, 0) in the preview canvas
	renderer.clearDepth();
	renderer.setViewport(0, 0, canvasWidth, canvasHeight);
	renderer.setScissor(0, 0, canvasWidth, canvasHeight);
	renderer.setScissorTest(true);
	renderer.render(orthoPreview.previewScene, orthoPreview.camera);
	renderer.setScissorTest(false);
}

export function updateOrthoPreviewOnResize(orthoPreview: OrthoPreviewCamera) {
	orthoPreview.updateCameraFrustum();
}
